import React, { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2, Megaphone } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ICON_OPTIONS = ["Trophy", "Users", "Clock", "Heart", "Target", "Zap", "Sparkles", "Cpu", "ShieldCheck"];

const TextInput = ({ value, onChange, ...rest }) => (
  <input
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    className="w-full bg-black/60 border border-white/15 rounded-md px-3 py-2 text-sm text-spike-text placeholder:text-spike-dim focus:outline-none focus:border-spike-blue"
    {...rest}
  />
);

const TextArea = ({ value, onChange, rows = 3, ...rest }) => (
  <textarea
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    className="w-full bg-black/60 border border-white/15 rounded-md px-3 py-2 text-sm text-spike-text placeholder:text-spike-dim focus:outline-none focus:border-spike-blue"
    {...rest}
  />
);

const SectionCard = ({ title, children, action }) => (
  <section className="bg-spike-elevated border border-white/10 rounded-lg p-5">
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-orbitron text-sm font-bold uppercase tracking-[0.2em] text-spike-blue-light">{title}</h2>
      {action}
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

const AdminHome = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/home/content");
      setContent(data);
    } catch (e) {
      toast.error("Could not load home content");
    } finally {
      setLoading(false);
    }
  })(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/home/content", content, { withCredentials: true });
      toast.success("Home content updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !content) return <div className="flex items-center gap-2 text-spike-dim p-6"><Loader2 className="w-4 h-4 animate-spin"/> Loading...</div>;

  const setField = (key, val) => setContent({ ...content, [key]: val });
  const setNested = (key, sub, val) => setContent({ ...content, [key]: { ...content[key], [sub]: val } });
  const updateArr = (key, idx, sub, val) => {
    const arr = [...(content[key] || [])];
    arr[idx] = { ...arr[idx], [sub]: val };
    setContent({ ...content, [key]: arr });
  };
  const addArr = (key, blank) => setContent({ ...content, [key]: [...(content[key] || []), blank] });
  const removeArr = (key, idx) => {
    const arr = [...(content[key] || [])];
    arr.splice(idx, 1);
    setContent({ ...content, [key]: arr });
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-home-cms">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-orbitron text-2xl font-black uppercase text-spike-text">Home Content</h1>
          <p className="text-sm text-spike-text-muted mt-1">Edit every section that visitors see on the home page.</p>
        </div>
        <button
          type="button"
          data-testid="admin-home-save-btn"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-black font-orbitron text-xs font-bold uppercase tracking-[0.18em] px-5 py-2.5 rounded-sm"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Save All
        </button>
      </div>

      <SectionCard title="Announcement Banner">
        <label className="flex items-center gap-2 text-sm text-spike-text" data-testid="cms-announcement-toggle">
          <input
            type="checkbox"
            data-testid="cms-announcement-enabled"
            checked={!!content.announcement?.enabled}
            onChange={(e) => setNested("announcement", "enabled", e.target.checked)}
          />
          Show banner at the top of the home page
        </label>
        <TextInput data-testid="cms-announcement-text" placeholder="Announcement text" value={content.announcement?.text} onChange={(v) => setNested("announcement", "text", v)} />
        <TextInput data-testid="cms-announcement-link" placeholder="Optional link URL" value={content.announcement?.link} onChange={(v) => setNested("announcement", "link", v)} />
      </SectionCard>

      <SectionCard
        title={`Hero Slides (${(content.hero_slides || []).length})`}
        action={<button type="button" onClick={() => addArr("hero_slides", { image: "", eyebrow: "", title: "", title_accent: "", body: "" })} className="text-xs inline-flex items-center gap-1 text-spike-blue-light"><Plus className="w-3 h-3"/>Add slide</button>}
      >
        {(content.hero_slides || []).map((s, i) => (
          <div key={i} className="bg-black/40 border border-white/10 rounded-md p-4 space-y-2" data-testid={`admin-home-hero-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-orbitron uppercase tracking-wider text-spike-blue-light">Slide {i + 1}</span>
              <button type="button" onClick={() => removeArr("hero_slides", i)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4"/></button>
            </div>
            <TextInput placeholder="Image URL" value={s.image} onChange={(v) => updateArr("hero_slides", i, "image", v)} />
            <TextInput placeholder="Eyebrow (small label above title)" value={s.eyebrow} onChange={(v) => updateArr("hero_slides", i, "eyebrow", v)} />
            <div className="grid grid-cols-2 gap-2">
              <TextInput placeholder="Title (first part)" value={s.title} onChange={(v) => updateArr("hero_slides", i, "title", v)} />
              <TextInput placeholder="Title (accent / highlighted)" value={s.title_accent} onChange={(v) => updateArr("hero_slides", i, "title_accent", v)} />
            </div>
            <TextArea placeholder="Body copy" value={s.body} onChange={(v) => updateArr("hero_slides", i, "body", v)} />
          </div>
        ))}
      </SectionCard>

      <SectionCard
        title={`Why Spike — Benefits (${(content.benefits || []).length})`}
        action={<button type="button" onClick={() => addArr("benefits", { icon: "Trophy", title: "", body: "" })} className="text-xs inline-flex items-center gap-1 text-spike-blue-light"><Plus className="w-3 h-3"/>Add</button>}
      >
        {(content.benefits || []).map((b, i) => (
          <div key={i} className="bg-black/40 border border-white/10 rounded-md p-4 space-y-2" data-testid={`admin-home-benefit-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-orbitron uppercase tracking-wider text-spike-blue-light">Benefit {i + 1}</span>
              <button type="button" onClick={() => removeArr("benefits", i)} className="text-red-400"><Trash2 className="w-4 h-4"/></button>
            </div>
            <select value={b.icon} onChange={(e) => updateArr("benefits", i, "icon", e.target.value)} className="w-full bg-black/60 border border-white/15 rounded-md px-3 py-2 text-sm text-spike-text">
              {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
            </select>
            <TextInput placeholder="Title" value={b.title} onChange={(v) => updateArr("benefits", i, "title", v)} />
            <TextArea placeholder="Body" value={b.body} onChange={(v) => updateArr("benefits", i, "body", v)} />
          </div>
        ))}
      </SectionCard>

      <SectionCard
        title={`How It Works (${(content.how_it_works || []).length})`}
        action={<button type="button" onClick={() => addArr("how_it_works", { n: "", title: "", body: "" })} className="text-xs inline-flex items-center gap-1 text-spike-blue-light"><Plus className="w-3 h-3"/>Add step</button>}
      >
        {(content.how_it_works || []).map((s, i) => (
          <div key={i} className="bg-black/40 border border-white/10 rounded-md p-4 space-y-2" data-testid={`admin-home-step-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-orbitron uppercase tracking-wider text-spike-blue-light">Step {i + 1}</span>
              <button type="button" onClick={() => removeArr("how_it_works", i)} className="text-red-400"><Trash2 className="w-4 h-4"/></button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <TextInput placeholder="01" value={s.n} onChange={(v) => updateArr("how_it_works", i, "n", v)} />
              <TextInput placeholder="Title" value={s.title} onChange={(v) => updateArr("how_it_works", i, "title", v)} className="col-span-2" />
            </div>
            <TextArea placeholder="Body" value={s.body} onChange={(v) => updateArr("how_it_works", i, "body", v)} />
          </div>
        ))}
      </SectionCard>

      <SectionCard title="Contact Info">
        <div className="grid grid-cols-2 gap-3">
          <TextInput data-testid="admin-home-contact-phone" placeholder="Phone" value={content.contact?.phone} onChange={(v) => setNested("contact", "phone", v)} />
          <TextInput data-testid="admin-home-contact-email" placeholder="Email" value={content.contact?.email} onChange={(v) => setNested("contact", "email", v)} />
        </div>
        <TextInput data-testid="admin-home-contact-address" placeholder="Address" value={content.contact?.address} onChange={(v) => setNested("contact", "address", v)} />
      </SectionCard>

      <div className="sticky bottom-0 bg-black/80 backdrop-blur border-t border-white/10 py-3 -mx-6 px-6 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-black font-orbitron text-xs font-bold uppercase tracking-[0.18em] px-5 py-2.5 rounded-sm"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Save All
        </button>
      </div>
    </div>
  );
};

export default AdminHome;
